import {
  CardEditConfig,
  FormState,
  MediaFile,
  FieldDefinition,
  ValidationRule
} from './types';
import { fieldRegistry } from './fieldRegistry';
import './registerFields';

export type FieldValueAccessor<T> = (
  formState: T,
  fieldName: string
) => string | MediaFile | null;

interface ValidationResult {
  valid: boolean;
  error: string | null;
  fieldName?: string;
}

const isValuePresent = (value: string | MediaFile | null): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null;
};

const validateField = <T>(
  field: FieldDefinition,
  formState: T,
  accessor: FieldValueAccessor<T>
): ValidationResult => {
  const value = accessor(formState, field.name);
  const label = field.label || field.name;

  if (!field.validation) {
    return { valid: true, error: null };
  }

  switch (field.validation.type) {
    case 'required': {
      const validator = fieldRegistry.getValidator(field.type);
      if (validator) {
        const result = validator(field, value);
        if (result && !result.valid) {
          return {
            valid: result.valid,
            error: result.error || null,
            fieldName: result.fieldName
          };
        }
      }
      break;
    }

    case 'atLeastOne':
      break;

    default:
      console.warn(
        `Unknown validation type: ${(field.validation as ValidationRule & { type: string }).type}`
      );
  }

  return { valid: true, error: null };
};

export const getFieldValue = (
  formState: FormState,
  fieldName: string
): string | MediaFile | null => {
  return formState[fieldName] as string | MediaFile | null;
};

export const validateForm = <T extends Record<string, unknown>>(
  config: CardEditConfig,
  formState: T,
  fieldValueAccessor: FieldValueAccessor<T> = getFieldValue as FieldValueAccessor<T>
): string | null => {
  if (config.validation?.type === 'atLeastOne') {
    const { fields } = config.validation;
    const hasAtLeastOne = fields.some(fieldName => {
      const value = fieldValueAccessor(formState, fieldName);
      return isValuePresent(value);
    });
    if (!hasAtLeastOne) {
      const fieldLabels = config.fields
        .filter(f => fields.includes(f.name))
        .map(f => f.label || f.name)
        .join(' or ');
      return `${fieldLabels} is required`;
    }
  }

  for (const field of config.fields) {
    if (field.validation) {
      const validationResult = validateField(
        field,
        formState,
        fieldValueAccessor
      );
      if (!validationResult.valid) {
        return validationResult.error;
      }
    }
  }

  return null;
};
